import axios, { AxiosRequestConfig } from 'axios'
import { forEach } from 'lodash'

import {
  interWbHttp,
  interWbNavigate,
  interWbSession,
  IWbAppInfo,
  IWbHttpData,
} from '@interco/inter-webview-bridge'
import { enableDarkMode } from '@interco/inter-ui'

import { IS_APP, IS_DEV_ENV, MOCK_APP_INFO, Routes, SUCCESS_STATUS_CODE } from './constants'
import { traceData, TraceDataProps } from './services'
import Data from './api'

type TFileExtensions = '.pdf' | '.json'
type TBridgeUserInfo = Awaited<ReturnType<typeof interWbSession.getUserInfo>>
type TBridgePermissions = Awaited<ReturnType<typeof interWbSession.getPermissions>>
type TAnalyticsProduct = 'privacy_center' | 'privacy_policy_acceptance'

interface TBaseEvent {
  product_name?: TAnalyticsProduct
  product?: TAnalyticsProduct
  flow?: string
  step: string
  screen_name?: Routes
  scenario?: string
}

interface TEvent extends TBaseEvent {
  event_name?: string
}

interface TError extends TBaseEvent {
  event_name?: string
  error_description?: string
  error_message?: string
}

interface TSelectContent extends TBaseEvent {
  event_name?: string
  element_name: string
  element_type: string
  parent_name?: string
}

interface TAsyncEventData extends TBaseEvent {
  event_name?: string
  eventData: unknown
}

const logBridgeError = (operationName: string, error: unknown) => {
  if (IS_DEV_ENV) {
    global.console.debug(`Failed to ${operationName}`, error)
  }
}

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

const isValidAppInfo = (value: unknown): value is IWbAppInfo =>
  isObjectRecord(value) && isNonEmptyString(value.language) && isNonEmptyString(value.organization)

const isValidUserInfo = (value: unknown): value is TBridgeUserInfo =>
  isObjectRecord(value) && isNonEmptyString(value.account)

const isValidPermissions = (value: unknown): value is TBridgePermissions => Array.isArray(value)

const SUPPORTED_BRIDGE_HTTP_METHODS = new Set(['GET', 'POST', 'PUT', 'DELETE'])

const validateBridgeRequest = (request: Data) => {
  if (!isNonEmptyString(request.action)) {
    throw new Error('Bridge request action is required')
  }

  if (!isNonEmptyString(request.metadata.endpoint) || !isNonEmptyString(request.metadata.method)) {
    throw new Error('Bridge request metadata is invalid')
  }

  if (!SUPPORTED_BRIDGE_HTTP_METHODS.has(request.metadata.method.toUpperCase())) {
    throw new Error(`Unsupported bridge HTTP method: ${request.metadata.method}`)
  }
}

const normalizeBridgeHeaders = (headers?: Data['metadata']['headers']): Record<string, string> => {
  if (!headers || typeof headers !== 'object') {
    return {}
  }

  return Object.entries(headers).reduce<Record<string, string>>((acc, [key, value]) => {
    if (value === undefined) {
      return acc
    }

    if (typeof value === 'string') {
      acc[key] = value
    } else if (typeof value === 'number') {
      acc[key] = String(value)
    } else if (Array.isArray(value)) {
      acc[key] = value.join(', ')
    }

    return acc
  }, {})
}

const isInterWbHttpAvailable = (): boolean =>
  Boolean(
    interWbHttp &&
      typeof interWbHttp.get === 'function' &&
      typeof interWbHttp.post === 'function' &&
      typeof interWbHttp.put === 'function' &&
      typeof interWbHttp.delete === 'function',
  )

const executeInterWbHttp = (request: Data, isUSBranch?: boolean): Promise<IWbHttpData> => {
  const method = request.metadata.method.toUpperCase()
  const { endpoint } = request.metadata
  const headers = normalizeBridgeHeaders(request.metadata.headers)
  const httpOptions = { timeout: 30000 }
  const body = (request.metadata.body ?? {}) as Record<string, unknown>

  const org: { org?: 'US_BRANCH' } = isUSBranch ? { org: 'US_BRANCH' } : {}

  switch (method) {
    case 'GET':
      return interWbHttp.get({
        endpoint,
        headers,
        options: httpOptions,
        ...org,
      })
    case 'POST':
      return interWbHttp.post({
        endpoint,
        body,
        headers,
        options: httpOptions,
        ...org,
      })
    case 'PUT':
      return interWbHttp.put({
        endpoint,
        body,
        headers,
        options: httpOptions,
        ...org,
      })
    case 'DELETE':
      return interWbHttp.delete({
        endpoint,
        headers,
        options: httpOptions,
        ...org,
      })
    default:
      throw new Error(`Unsupported bridge HTTP method: ${request.metadata.method}`)
  }
}

const parseBridgeResponse = (data: IWbHttpData, returnAllResponse?: boolean) => {
  const isNullResponse = !data.response || data.response === 'null' || data.response === null

  if (isNullResponse) {
    return returnAllResponse ? { ...data, response: {} } : null
  }

  try {
    const response = JSON.parse(data.response)
    return returnAllResponse ? { ...data, response } : response
  } catch (error: unknown) {
    logBridgeError('parse bridge response', error)
    throw new Error('Invalid bridge response payload')
  }
}

const validateAnalyticsPayload = (eventName: string, params: Record<string, string>) => {
  if (!isNonEmptyString(eventName)) {
    throw new Error('Analytics event name is required')
  }

  if (!isObjectRecord(params)) {
    throw new Error('Analytics params are invalid')
  }
}

const toSnakeCase = (value: string): string =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLocaleLowerCase()

const makeScreenName = ({ product_name, flow, step }: TBaseEvent) =>
  `${product_name}/${flow}/${step}`

const getAnalyticsProduct = (flow?: string): TAnalyticsProduct =>
  flow?.includes('privacy_policy_acceptance') ? 'privacy_policy_acceptance' : 'privacy_center'

const removeUndefinedAnalyticsParams = (params: Record<string, string | undefined>) =>
  Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
    if (value !== undefined) acc[key] = value

    return acc
  }, {})

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback

const serializeError = (error: unknown) => {
  const visitedObjects = new WeakSet<Record<string, unknown>>()

  try {
    return (
      JSON.stringify(error, (_key, value: unknown) => {
        if (typeof value === 'bigint') {
          return value.toString()
        }

        if (!isObjectRecord(value)) {
          return value
        }

        if (visitedObjects.has(value)) {
          return '[Circular]'
        }

        visitedObjects.add(value)

        if (!(value instanceof Error)) {
          return value
        }

        return Object.getOwnPropertyNames(value).reduce<Record<string, unknown>>(
          (serializedError, propertyName) => {
            serializedError[propertyName] = Reflect.get(value, propertyName)
            return serializedError
          },
          { name: value.name },
        )
      }) ?? getErrorMessage(error, 'Unable to serialize error')
    )
  } catch (serializationError: unknown) {
    logBridgeError('serialize error', serializationError)
    return getErrorMessage(error, 'Unable to serialize error')
  }
}

const makeRequestURL = (
  url: string,
  pathParams: Record<string, string>,
  fileExtension?: TFileExtensions,
) =>
  Object.entries(pathParams)
    .reduce(
      (currentUrl, [pathParam, value]) =>
        currentUrl.replace(`:${pathParam}`, encodeURIComponent(value)),
      url,
    )
    .concat(fileExtension ?? '')

const makeBridgeRequest = async (
  request: Data,
  context?: TraceDataProps['context'],
  returnAllResponse?: boolean,
  isUSBranch?: boolean,
) => {
  validateBridgeRequest(request)

  traceData({
    data: {
      transactionType: 'BRIDGE_API_CALL',
      transactionSubType: `${request.metadata.method} - ${extractEndpoint(
        request.metadata.endpoint,
      )} `,
      status: 'REQUESTING',
      detail: request.metadata.body ? JSON.stringify(request.metadata.body) : '-',
    },
    context,
  })

  if (!IS_APP) return mockRequest(request)

  if (!isInterWbHttpAvailable()) {
    throw new Error('interWbHttp is unavailable')
  }

  let data: IWbHttpData

  try {
    data = await executeInterWbHttp(request, isUSBranch)
  } catch (error: unknown) {
    logBridgeError('execute bridge request', error)

    try {
      await sendError({
        flow: 'bridge_request',
        step: toSnakeCase(request.metadata.method),
        screen_name: Routes.ROOT,
        error_description: extractEndpoint(request.metadata.endpoint),
        error_message: getErrorMessage(error, 'Bridge request failed'),
      })
    } catch (analyticsError: unknown) {
      logBridgeError('send bridge error analytics', analyticsError)
    }

    traceData({
      data: {
        transactionType: 'BRIDGE_API_CALL',
        transactionSubType: `${request.metadata.method} - ${extractEndpoint(
          request.metadata.endpoint,
        )} `,
        status: 'ERROR',
        detail: getErrorMessage(error, 'Bridge request failed'),
      },
      context,
    })

    throw error
  }

  // -- logs SUCCESS
  if (SUCCESS_STATUS_CODE.includes(Number(data.httpStatus))) {
    const response = parseBridgeResponse(data, returnAllResponse)

    traceData({
      data: {
        transactionType: 'BRIDGE_API_CALL',
        transactionSubType: `${request.metadata.method} - ${extractEndpoint(
          request.metadata.endpoint,
        )} `,
        status: 'SUCCESS',
        detail: data.response,
      },
      context,
    })

    return response
  }

  // -- logs ERROR
  try {
    await sendError({
      flow: 'bridge_request',
      step: toSnakeCase(request.metadata.method),
      screen_name: Routes.ROOT,
      error_description: extractEndpoint(request.metadata.endpoint),
      error_message: JSON.stringify(data),
    })
  } catch (analyticsError: unknown) {
    logBridgeError('send bridge response error analytics', analyticsError)
  }

  traceData({
    data: {
      transactionType: 'BRIDGE_API_CALL',
      transactionSubType: `${request.metadata.method} - ${extractEndpoint(
        request.metadata.endpoint,
      )} `,
      status: 'ERROR',
      detail: JSON.stringify(data),
    },
    context,
  })

  throw new Error('Bridge request failed')
}

const makeHttpRequest = async <T>(
  request: AxiosRequestConfig,
  context?: TraceDataProps['context'],
): Promise<T> => {
  traceData({
    data: {
      transactionType: 'HTTP_API_CALL',
      transactionSubType: `${request.method} - ${request.url}`,
      status: 'REQUESTING',
      detail: request.data ? JSON.stringify(request.data) : '-',
    },
    context,
  })

  const { data, status } = await axios<T>(request)

  // -- logs SUCCESS
  if (SUCCESS_STATUS_CODE.includes(Number(status))) {
    traceData({
      data: {
        transactionType: 'HTTP_API_CALL',
        transactionSubType: `${request.method} - ${request.url}`,
        status: 'SUCCESS',
        detail: data ? JSON.stringify(request.data) : '-',
      },
      context,
    })

    return data
  }

  // -- logs ERROR
  traceData({
    data: {
      transactionType: 'HTTP_API_CALL',
      transactionSubType: `${request.method} - ${request.url}`,
      status: 'ERROR',
      detail: request.data ? JSON.stringify(request.data) : '-',
    },
    context,
  })

  return data
}

const mockRequest = async (request: Data, context?: TraceDataProps['context']) => {
  const response = await fetch(request.metadata.endpoint, {
    method: request.metadata.method,
    headers: { responseType: 'arraybuffer' },
    body: request.metadata.body ? JSON.stringify(request.metadata.body) : null,
  })

  traceData({
    data: {
      transactionType: 'BRIDGE_API_CALL',
      transactionSubType: `${request.metadata.method} - ${extractEndpoint(
        request.metadata.endpoint,
      )} `,
      status: response.ok ? 'SUCCESS' : 'ERROR',
      detail: 'localMockResponse',
    },
    context,
  })

  if (request.metadata.endpoint.includes('pdf')) return response.arrayBuffer()

  if (response.ok) return response.json().catch(() => null)

  const errorResponse = await response.json().catch(() => null)

  return Promise.reject(new Error(errorResponse?.message ?? 'Unknown error'))
}

const extractEndpoint = (endpoint: string) => {
  const regex = /\/v\d+(.*)/

  const matches = regex.exec(endpoint)
  return matches ? matches[1] : ''
}

const leaveCentralDePrivacidade = () => {
  if (IS_APP) interWbNavigate.requestGoBack()

  if (!IS_APP && IS_DEV_ENV) global.console.log('Would return to Super App`s home')
}

const getAppInfo = async () => {
  if (!IS_APP) return MOCK_APP_INFO

  if (typeof interWbSession.getAppInfo !== 'function') {
    logBridgeError('getAppInfo', new Error('interWbSession.getAppInfo is unavailable'))
    return MOCK_APP_INFO
  }

  try {
    const appInfo = await interWbSession.getAppInfo()

    if (!isValidAppInfo(appInfo)) {
      throw new Error('Invalid app info response')
    }

    return appInfo
  } catch (error: unknown) {
    logBridgeError('getAppInfo', error)
    return MOCK_APP_INFO
  }
}

const getUserInfo = async () => {
  if (typeof interWbSession.getUserInfo !== 'function') {
    throw new Error('interWbSession.getUserInfo is unavailable')
  }

  try {
    const userInfo = await interWbSession.getUserInfo()

    if (!isValidUserInfo(userInfo)) {
      throw new Error('Invalid user info response')
    }

    return userInfo
  } catch (error: unknown) {
    logBridgeError('getUserInfo', error)
    throw error
  }
}

const getUserPermissions = async () => {
  if (typeof interWbSession.getPermissions !== 'function') {
    throw new Error('interWbSession.getPermissions is unavailable')
  }

  try {
    const permissions = await interWbSession.getPermissions()

    if (!isValidPermissions(permissions)) {
      throw new Error('Invalid permissions response')
    }

    return permissions
  } catch (error: unknown) {
    logBridgeError('getUserPermissions', error)
    throw error
  }
}

const requestAnalytics = async (
  eventName: string,
  params: Record<string, string>,
): Promise<void> => {
  validateAnalyticsPayload(eventName, params)

  if (!IS_APP) return

  if (typeof interWbSession.requestAnalytics !== 'function') {
    throw new Error('interWbSession.requestAnalytics is unavailable')
  }

  try {
    await interWbSession.requestAnalytics(eventName, params)
  } catch (error: unknown) {
    logBridgeError('requestAnalytics', error)
    throw error
  }
}

const sendScreenView = async ({
  product_name,
  product,
  flow = 'privacy_center',
  step,
  screen_name,
  scenario,
  event_name = 'screen_view',
}: TEvent): Promise<void> => {
  const analyticsProduct = product_name ?? getAnalyticsProduct(flow)
  const analyticsEvent = {
    product_name: analyticsProduct,
    product: product ?? analyticsProduct,
    flow,
    step,
    screen_name: screen_name ?? makeScreenName({ product_name: analyticsProduct, flow, step }),
    scenario,
    event_name,
  }

  return requestAnalytics(event_name, removeUndefinedAnalyticsParams(analyticsEvent))
}

const sendError = async ({
  product_name,
  product,
  flow = 'privacy_center',
  step,
  screen_name,
  scenario,
  error_description,
  error_message,
  event_name = 'error',
}: TError): Promise<void> => {
  const analyticsProduct = product_name ?? getAnalyticsProduct(flow)
  const analyticsEvent = {
    product_name: analyticsProduct,
    product: product ?? analyticsProduct,
    flow,
    step,
    screen_name: screen_name ?? makeScreenName({ product_name: analyticsProduct, flow, step }),
    scenario,
    error_description,
    error_message,
    event_name,
  }

  return requestAnalytics(event_name, removeUndefinedAnalyticsParams(analyticsEvent))
}

const sendSelectContent = async ({
  product_name,
  product,
  flow = 'privacy_center',
  step,
  screen_name,
  scenario,
  element_name,
  element_type,
  parent_name,
  event_name = 'select_content',
}: TSelectContent): Promise<void> => {
  const analyticsProduct = product_name ?? getAnalyticsProduct(flow)
  const analyticsEvent = {
    product_name: analyticsProduct,
    product: product ?? analyticsProduct,
    flow,
    step,
    screen_name: screen_name ?? makeScreenName({ product_name: analyticsProduct, flow, step }),
    scenario,
    element_name: element_name.replaceAll(' ', '_').toLocaleLowerCase(),
    element_type,
    parent_name,
    event_name,
  }

  return requestAnalytics(event_name, removeUndefinedAnalyticsParams(analyticsEvent))
}

const sendAsyncEventData = async ({
  product_name,
  product,
  flow = 'privacy_center',
  step,
  screen_name,
  eventData,
  event_name = 'async_event',
}: TAsyncEventData): Promise<void> => {
  const analyticsProduct = product_name ?? getAnalyticsProduct(flow)
  const analyticsEvent = {
    product_name: analyticsProduct,
    product: product ?? analyticsProduct,
    flow,
    step,
    screen_name: screen_name ?? makeScreenName({ product_name: analyticsProduct, flow, step }),
    scenario: JSON.stringify({ eventData }),
    event_name,
  }

  try {
    await requestAnalytics(event_name, removeUndefinedAnalyticsParams(analyticsEvent))
  } catch (error: unknown) {
    logBridgeError(`send ${step} async event data`, error)
  }
}

const copyToClipboard = async (key: string, value: string): Promise<void> => {
  if (!isNonEmptyString(key) || !isNonEmptyString(value)) {
    throw new Error('Clipboard key and value are required')
  }

  if (!IS_APP) {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return
    }

    throw new Error('Clipboard API is unavailable')
  }

  if (typeof interWbSession.copyToClipboard !== 'function') {
    throw new Error('interWbSession.copyToClipboard is unavailable')
  }

  try {
    await interWbSession.copyToClipboard(key, value)
  } catch (error: unknown) {
    logBridgeError('copyToClipboard', error)
    throw error
  }
}

const setDarkMode = () => {
  const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches

  if (isDark) enableDarkMode()
}

const makeTraceDetails = (isActive: boolean, component?: Record<string, unknown>): string => {
  if (!isActive) return `Component is inactive`

  let fields = ''

  if (component) {
    forEach(component, (value, field) => {
      fields += `${field} = ${value ? JSON.stringify(value) : 'null'}, `
    })
  }

  // Remove trailing comma and space before returning
  return `${fields.slice(0, -2)}`
}

export {
  makeRequestURL,
  IS_APP,
  makeBridgeRequest,
  makeHttpRequest,
  leaveCentralDePrivacidade,
  getAppInfo,
  getUserInfo,
  makeScreenName,
  toSnakeCase,
  serializeError,
  sendScreenView,
  sendSelectContent,
  sendError,
  sendAsyncEventData,
  setDarkMode,
  makeTraceDetails,
  getUserPermissions,
  requestAnalytics,
  copyToClipboard,
  logBridgeError,
}
